import { Link } from "react-router-dom";
import { ArrowRight, Calendar, Clock, ChevronLeft, MapPin } from "lucide-react";
import Seo from "@/components/Seo";

const PATH = "/blog/mice-asia-trends-2026";
const TITLE = "MICE in Asia 2026: Seven Trends Every Corporate Planner Should Track";
const DESCRIPTION =
  "From AI-driven RFP processes and sustainability-linked KPIs to the rise of secondary Asian cities for MICE — the seven trends shaping Asian MICE in 2026.";
const PUBLISHED_ISO = "2026-02-17";
const PUBLISHED_LABEL = "Feb 17, 2026";
const AUTHOR = "Trawelldmc Team";
const IMAGE =
  "https://images.unsplash.com/photo-1517245386807-bb43f82c33c4?crop=entropy&cs=srgb&fm=webp&w=1600&q=80";

const BREADCRUMB = [
  { name: "Home", path: "/" },
  { name: "Blog", path: "/blog" },
  { name: "MICE Asia 2026 Trends", path: PATH },
];

const ARTICLE_JSONLD = {
  "@context": "https://schema.org",
  "@type": "Article",
  headline: TITLE,
  description: DESCRIPTION,
  image: IMAGE,
  datePublished: PUBLISHED_ISO,
  dateModified: PUBLISHED_ISO,
  author: { "@type": "Organization", name: AUTHOR, url: "https://www.trawelldmc.com" },
  publisher: {
    "@type": "Organization",
    name: "Trawelldmc",
    logo: { "@type": "ImageObject", url: "https://www.trawelldmc.com/trawelldmc-logo-hd.png" },
  },
  mainEntityOfPage: { "@type": "WebPage", "@id": `https://www.trawelldmc.com${PATH}` },
};

const TRENDS = [
  {
    title: "AI is rewriting the RFP cycle",
    copy:
      "Corporate planners increasingly use AI tools to generate first-draft briefs and to compare vendor proposals. DMCs that respond with AI-augmented first quotes — like our TrawellAI concierge — compress the buying cycle from weeks to days.",
  },
  {
    title: "Sustainability is now a KPI, not a tagline",
    copy:
      "Procurement teams are auditing event-level CO₂ output, in-room single-use plastics, and supplier diversity scores. Expect to provide a measurable sustainability appendix in every Tier-1 MICE RFP from 2026 onwards.",
  },
  {
    title: "Secondary cities are stealing share",
    copy:
      "Chiang Mai, Da Nang, Cebu, Luang Prabang and Jeju are seeing 30–50% YoY growth in incentive bookings as planners seek differentiation from over-saturated Tier-1 cities. Our 11-destination map deliberately mirrors this trend.",
  },
  {
    title: "Hybrid events have plateaued — premium in-person is rising",
    copy:
      "After the COVID-era hybrid push, 2025 data shows in-person budgets up 18% while pure virtual budgets have flattened. The new premium is &ldquo;why-fly-there&rdquo; content — galas, cultural moments, content nobody else can buy.",
  },
  {
    title: "Visa & passport diversity is the planner&apos;s new headache",
    copy:
      "Cross-border Asian groups routinely involve 12–15 passport jurisdictions. DMCs that handle visa documentation in-house — with weekly status reports — are winning on operational reliability, not just price.",
  },
  {
    title: "Content-capture is bundled, not bolted-on",
    copy:
      "Channel partners, employee groups and incentive winners all want shareable content. 2026 RFPs increasingly mandate a 4–6 person content production unit on the operating team. Plan for it from the start.",
  },
  {
    title: "Multi-country itineraries are back",
    copy:
      "After years of single-city programmes, 2026 is seeing a return to Bangkok+Phuket, Hanoi+Ha Long, KL+Langkawi splits — and even Singapore+Bali. The DMC that handles all legs under one contract wins.",
  },
];

export default function MiceAsiaTrends2026() {
  return (
    <div data-testid="mice-asia-trends-2026">
      <Seo
        path={PATH}
        title={`${TITLE} | Trawelldmc Insights`}
        description={DESCRIPTION}
        type="article"
        image={IMAGE}
        breadcrumb={BREADCRUMB}
        extraJsonLd={ARTICLE_JSONLD}
      />

      <section className="bg-[#0B192C] text-white">
        <div className="max-w-3xl mx-auto px-6 md:px-10 py-20 md:py-24">
          <Link to="/blog" className="inline-flex items-center gap-1.5 text-sm text-white/60 hover:text-white mb-8">
            <ChevronLeft className="h-4 w-4" /> Back to Blog
          </Link>
          <div className="overline !text-[#FF5238] mb-5">MICE · Industry Insight</div>
          <h1 className="font-heading text-4xl md:text-5xl lg:text-6xl font-light tracking-tight leading-[1.08]">
            {TITLE}
          </h1>
          <div className="mt-8 flex items-center gap-5 text-sm text-white/60 flex-wrap">
            <span className="inline-flex items-center gap-1.5"><Calendar className="h-3.5 w-3.5" /> {PUBLISHED_LABEL}</span>
            <span className="inline-flex items-center gap-1.5"><Clock className="h-3.5 w-3.5" /> 8 min read</span>
            <span className="inline-flex items-center gap-1.5"><MapPin className="h-3.5 w-3.5" /> Asia DMC</span>
            <span>By {AUTHOR}</span>
          </div>
        </div>
      </section>

      <section>
        <div className="max-w-5xl mx-auto px-6 md:px-10 -mt-10 mb-12">
          <img
            src={IMAGE}
            alt="MICE conference Asia 2026 — Trawelldmc industry insight"
            width="1600"
            height="900"
            loading="lazy"
            decoding="async"
            className="w-full aspect-[16/9] object-cover rounded-md shadow-xl"
          />
        </div>
      </section>

      <article className="max-w-3xl mx-auto px-6 md:px-10 pb-24">
        <p className="text-xl text-slate-700 leading-relaxed font-light">
          MICE in Asia is moving faster in 2026 than at any point since the
          pre-pandemic peak. Procurement teams are under cost pressure, planners
          face shorter lead times, and delegates increasingly judge programmes
          by what they can share online. Here are the seven trends that
          Trawelldmc&apos;s account teams are seeing across our 11 Asia DMC
          markets — and what they mean for your next event.
        </p>

        {TRENDS.map((t, i) => (
          <div key={t.title}>
            <h2 className="font-heading text-3xl md:text-4xl font-light tracking-tight text-[#0B192C] mt-12 mb-5 flex items-baseline gap-3">
              <span className="text-[#FF5238]">{String(i + 1).padStart(2, "0")}.</span>
              {t.title}
            </h2>
            <p className="text-slate-700 leading-relaxed">{t.copy}</p>
          </div>
        ))}

        <h2 className="font-heading text-3xl md:text-4xl font-light tracking-tight text-[#0B192C] mt-16 mb-5">
          What this means for your 2026 brief
        </h2>
        <p className="text-slate-700 leading-relaxed">
          The simplest test: when you write your next MICE RFP, can your
          shortlisted DMCs answer all seven trends with operating evidence —
          not promises? At Trawelldmc, we&apos;ve built our process around
          exactly that: AI-augmented first quotes, sustainability tracking,
          secondary-city expertise across Cambodia, Laos and Vietnam, content
          production bundled into operations, and visa cells inside every
          destination team. Two decades of MICE operating depth, calibrated for
          the realities of 2026.
        </p>

        <div className="mt-12 p-8 bg-[#0B192C] text-white rounded-md">
          <div className="overline !text-[#FF5238] mb-3">Plan with us</div>
          <h3 className="font-heading text-2xl font-medium tracking-tight">
            Ready to brief Asia&apos;s MICE specialist?
          </h3>
          <p className="text-white/80 mt-3 leading-relaxed">
            Talk to a senior B2B account manager. AI first quote available now;
            human team responds within 48 hours.
          </p>
          <div className="mt-6 flex flex-wrap gap-3">
            <Link to="/quote" className="btn-primary inline-flex">
              Try the AI Quote <ArrowRight className="h-4 w-4" />
            </Link>
            <Link to="/mice-services" className="btn-secondary !bg-white/10 !border-white/20 !text-white hover:!bg-white/20">
              Explore MICE services
            </Link>
          </div>
        </div>
      </article>
    </div>
  );
}
